import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/lib/supabase/client";

export const AuthContext = createContext(null);

const hasStoredSession = () => {
  try {
    return Object.keys(window.localStorage).some((k) => k.startsWith("sb-") && k.endsWith("-auth-token"));
  } catch {
    return false;
  }
};

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [signedOutManually, setSignedOutManually] = useState(false);
  const hadSession = useRef(hasStoredSession());
  const manualSignOut = useRef(false);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      if (data.session) hadSession.current = true;
      else if (hadSession.current) setSessionExpired(true);
      setSession(data.session);
      setLoading(false);
    });
    const { data } = supabase.auth.onAuthStateChange((event, next) => {
      if (!mounted) return;
      if (next) {
        hadSession.current = true;
        setSessionExpired(false);
        setSignedOutManually(false);
      } else if (event === "SIGNED_OUT") {
        if (hadSession.current && !manualSignOut.current) setSessionExpired(true);
        hadSession.current = false;
        manualSignOut.current = false;
      }
      setSession(next);
      setLoading(false);
    });
    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    manualSignOut.current = true;
    setSessionExpired(false);
    setSignedOutManually(true);
    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error) {
      manualSignOut.current = false;
      setSignedOutManually(false);
      throw error;
    }
  }, []);

  const value = useMemo(
    () => ({ session, user: session?.user ?? null, loading, sessionExpired, signedOutManually, signOut }),
    [session, loading, sessionExpired, signedOutManually, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
